import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  selectGroupGuid,
  selectUserGuid,
} from '../../../slices/commonSelectors';
import {
  TypeOptions,
  USCGResourceKindOptions,
} from 'components/IncidentConfig/ResourceTypes';

export type CustomResourceType = {
  id: string;
  name: string;
  type: string;
  description?: string;
  group_guid: string;
  archived: boolean;
};

export const keys = {
  base: 'customResources',
  get all() {
    return [keys.base];
  },
  list: (groupGuid: string, userId: string) => [keys.base, groupGuid, userId],
} as const;

export enum ResourceClassification {
  Kind = 'kind',
  Type = 'type',
}

export type ResourceClassificationType = `${ResourceClassification}`;

export type CreateCustomResourceReqData = {
  name: string;
  type: string;
  description?: string;
  group_guid: string;
  archived?: boolean;
};

interface EditCustomResourceReqData {
  id: string;
  name?: string;
  type?: string;
  description?: string;
  group_guid?: string;
  archived?: boolean;
}

export const useCustomResources = (
  resourceClassification?: ResourceClassification
) => {
  const user_guid = useSelector(selectUserGuid);
  const group_guid = useSelector(selectGroupGuid);
  return useQuery({
    queryKey: keys.list(group_guid, user_guid),
    queryFn: async () => {
      const resp = await axios.get<CustomResourceType[]>(
        `${config.apiGateway.ics_custom_resource_types}/${group_guid}`
      );
      return resp.data;
    },
    select: resourceClassification
      ? (data) =>
          data.filter((r) => {
            if (r.name && resourceClassification === 'kind') return true;
            if (r.type && resourceClassification === 'type') return true;
            return false;
          })
      : undefined,
  });
};

export const useCreateCustomResource = () => {
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const group_guid = useSelector(selectGroupGuid);
  const mutation = useMutation({
    mutationFn: async (data: CreateCustomResourceReqData) => {
      return await axios.post(
        `${config.apiGateway.ics_custom_resource_types}`,
        { ...data, user_guid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(group_guid, user_guid),
      });
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });
  return mutation;
};

export const useEditCustomResource = (customResourceId: string) => {
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const group_guid = useSelector(selectGroupGuid);
  return useMutation({
    mutationFn: async (data: EditCustomResourceReqData) => {
      return await axios.put(
        `${config.apiGateway.ics_custom_resource_types}/${customResourceId}`,
        { ...data, user_guid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(group_guid, user_guid),
      });
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });
};

export const useCustomResourcePersistence = () => {
  const groupGuid = useSelector(selectGroupGuid);
  const customResourceMutation = useCreateCustomResource();
  const { data: customKinds = [] } = useCustomResources(
    ResourceClassification.Kind
  );
  const { data: customTypes = [] } = useCustomResources(
    ResourceClassification.Type
  );

  const persistPendingResources = async (kind?: string, type?: string) => {
    if (kind) {
      const isCustomKind = !(
        customKinds.find((v) => v.name === kind) ||
        USCGResourceKindOptions.find((v) => v.value === kind)
      );

      if (isCustomKind) {
        await customResourceMutation.mutateAsync({
          name: kind,
          type: '',
          group_guid: groupGuid,
          archived: false,
        });
      }
    }

    if (type) {
      const isCustomType = !(
        customTypes.find((v) => v.type === type) ||
        TypeOptions.find((v) => v.value === type)
      );

      if (isCustomType) {
        await customResourceMutation.mutateAsync({
          name: '',
          type: type,
          group_guid: groupGuid,
          archived: false,
        });
      }
    }
  };

  return { persistPendingResources };
};
